import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FeatherFolder, FeatherCheckCircle, FeatherFileText, FeatherPackage } from '../ui/icons';
import { getProgressColor } from '../../utils/projectUtils';
import ProjectReviewReportModal from './Report';
import dailyReviewService from '../../services/dailyReviewService';
import './ProjectCard.css';

const ProjectCard = ({ project, date, releases = [] }) => {
    const navigate = useNavigate();
    const [showReport, setShowReport] = useState(false);
    const [isInitiating, setIsInitiating] = useState(false);

    const handleViewDetails = async () => {
        // If review is not initiated, initiate it first
        if (!project.isInitiated) {
            setIsInitiating(true);
            try {
                await dailyReviewService.initiateReview(project.id, date);
            } catch (error) {
                console.error('Error initiating review:', error);
                // Continue navigation even if initiation fails
            } finally {
                setIsInitiating(false);
            }
        }
        navigate(`/daily-work-plan-review/project/${project.id}?date=${date}`);
    };

    const getTrafficLightColor = () => {
        // If reviewed (completed), show blue regardless of pickup rate
        if (project.isReviewed) return 'blue';
        
        const { pickupStats } = project;
        if (!pickupStats) return 'gray';
        if (pickupStats.pickupRate === 100) return 'green';
        if (pickupStats.pickupRate >= 50) return 'yellow';
        return 'red';
    };

    const getButtonText = () => {
        if (!project.isInitiated) {
            return 'Initiate Review';
        } else if (project.isInitiated && !project.isReviewed) {
            return 'Continue Review';
        } else {
            return 'Re-verify';
        }
    };

    const { pickupStats } = project || {};

    // Calculate release statistics
    const totalReleases = releases.length;
    const activeReleases = releases.filter(r => r.status === 'in_progress').length;
    const completedReleases = releases.filter(r => r.status === 'completed').length;
    
    // Calculate overall release progress
    let totalReleaseProgress = 0;
    let releasesWithProgress = 0;
    releases.forEach(release => {
        if (release.insights && release.insights.completion_percentage !== undefined) {
            totalReleaseProgress += release.insights.completion_percentage;
            releasesWithProgress++;
        }
    });
    const avgReleaseProgress = releasesWithProgress > 0 
        ? Math.round(totalReleaseProgress / releasesWithProgress) 
        : 0;

    return (
        <div className="project-card">
            <div className="project-card-header">
                <div className="project-card-title-section">
                    <div className={`traffic-light ${getTrafficLightColor()}`} />
                    <div>
                        <div className="project-card-title-row">
                            <h3 className="project-card-title">{project.name}</h3>
                            {project.isInitiated && !project.isReviewed && (
                                <Badge variant="warning" className="project-card-reviewed-badge">
                                    <FeatherCheckCircle size={12} />
                                    <span>Initiated</span>
                                </Badge>
                            )}
                            {project.isReviewed && (
                                <Badge variant="success" className="project-card-reviewed-badge">
                                    <FeatherCheckCircle size={12} />
                                    <span>Reviewed</span>
                                </Badge>
                            )}
                        </div>
                        {project.abbreviation && (
                            <span className="project-card-abbreviation">{project.abbreviation}</span>
                        )}
                    </div>
                </div>
            </div>

            {pickupStats && (
                <div className="project-card-pickup-stats">
                    <div className="pickup-stat-item">
                        <span className="pickup-stat-label">Picked</span>
                        <span className="pickup-stat-value">{pickupStats.pickedUpToday}</span>
                    </div>
                    <div className="pickup-stat-item">
                        <span className="pickup-stat-label">Remaining</span>
                        <span className={`pickup-stat-value ${pickupStats.remaining > 0 ? 'warning' : 'success'}`}>
                            {pickupStats.remaining}
                        </span>
                    </div>
                    <div className="pickup-stat-item">
                        <span className="pickup-stat-label">Rate</span>
                        <span className={`pickup-stat-value ${getTrafficLightColor()}`}>
                            {pickupStats.pickupRate}%
                        </span>
                    </div>
                </div>
            )}

            <div className="project-card-stats">
                <div className="project-card-stat">
                    <span className="project-card-stat-label">Tasks in Work Plan</span>
                    <span className="project-card-stat-value">{project.taskCount}</span>
                </div>
                <div className="project-card-stat">
                    <span className="project-card-stat-label">Resources</span>
                    <span className="project-card-stat-value">{project.resourceCount}</span>
                </div>
            </div>

            {/* Release Stats */}
            {totalReleases > 0 && (
                <div className="project-card-releases">
                    <div className="release-stat-header">
                        <FeatherPackage size={16} />
                        <span className="release-stat-header-label">Releases</span>
                    </div>
                    <div className="release-stat-content">
                        <div className="release-stat-item">
                            <span className="release-stat-value">{totalReleases}</span>
                            <span className="release-stat-label">Total</span>
                        </div>
                        <div className="release-stat-item">
                            <span className="release-stat-value">{activeReleases}</span>
                            <span className="release-stat-label">Active</span>
                        </div>
                        <div className="release-stat-item">
                            <span className="release-stat-value">{completedReleases}</span>
                            <span className="release-stat-label">Done</span>
                        </div>
                    </div>
                    {avgReleaseProgress > 0 && (
                        <div className="release-progress-section">
                            <div className="release-progress-bar">
                                <div 
                                    className="release-progress-fill" 
                                    style={{ 
                                        width: `${avgReleaseProgress}%`,
                                        backgroundColor: getProgressColor(avgReleaseProgress)
                                    }} 
                                />
                            </div>
                            <span className="release-progress-text">{avgReleaseProgress}% avg</span>
                        </div>
                    )}
                </div>
            )}

            {(project.overdueFeaturesCount > 0 || project.dueSoonFeaturesCount > 0) && (
                <div className="project-card-features">
                    {project.overdueFeaturesCount > 0 && (
                        <Badge variant="error" className="project-card-feature-badge">
                            <div className="project-card-feature-badge-content">
                                <span>{project.overdueFeaturesCount} Overdue</span>
                                <span className="project-card-feature-badge-label">Feature</span>
                            </div>
                        </Badge>
                    )}
                    {project.dueSoonFeaturesCount > 0 && (
                        <Badge variant="warning" className="project-card-feature-badge">
                            <div className="project-card-feature-badge-content">
                                <span>{project.dueSoonFeaturesCount} Due Soon</span>
                                <span className="project-card-feature-badge-label">Feature</span>
                            </div>
                        </Badge>
                    )}
                </div>
            )}

            <div className="project-card-actions">
                {project.isReviewed && (
                    <Button 
                        variant="neutral-tertiary" 
                        icon={<FeatherFileText size={18} />}
                        onClick={() => setShowReport(true)}
                        title="View Review Report"
                        className="project-card-report-button"
                    />
                )}
                <Button 
                    variant="brand-primary" 
                    onClick={handleViewDetails}
                    disabled={isInitiating}
                >
                    {isInitiating ? 'Initiating...' : getButtonText()}
                </Button>
            </div>

            {/* Review Report Modal - Only show if project is reviewed */}
            {project.isReviewed && (
                <ProjectReviewReportModal
                    projectId={project.id}
                    projectName={project.name}
                    projectAbbreviation={project.abbreviation}
                    date={date}
                    isReviewed={project.isReviewed}
                    reviewedAt={project.reviewedAt}
                    isOpen={showReport}
                    onClose={() => setShowReport(false)}
                />
            )}
        </div>
    );
};

export default ProjectCard;
